import { Controller } from "@hotwired/stimulus"

/**
 * Equipment Controller
 * Manages dynamic add/remove of equipment to a report
 * Works exactly like report_welders_controller for consistency
 */
export default class extends Controller {
  static targets = ["container", "equipmentSelect", "tableBody", "hiddenFields"]
  static outlets = ["autosave"]

  connect() {
    console.log('[equipment] Controller connected')
    this.equipmentData = new Map() // Store equipment data by ID
    this.loadExistingEquipment()
  }

  /**
   * Load existing equipment from the table
   */
  loadExistingEquipment() {
    if (!this.hasTableBodyTarget) {
      console.log('[equipment] No table body target, checking for existing rows')
      // Try to find existing table rows
      const existingRows = this.element.querySelectorAll('tr[data-equipment-id]')
      if (existingRows.length > 0) {
        console.log('[equipment] Found existing rows:', existingRows.length)
        existingRows.forEach(row => {
          const equipmentId = row.dataset.equipmentId
          const cells = row.querySelectorAll('td')

          this.equipmentData.set(equipmentId, {
            id: equipmentId,
            typeName: cells[0]?.textContent?.trim() || '',
            name: cells[1]?.textContent?.trim() || '',
            maker: cells[2]?.textContent?.trim() || '',
            serialNo: cells[3]?.textContent?.trim() || ''
          })
        })
      }
      return
    }

    const rows = this.tableBodyTarget.querySelectorAll('tr[data-equipment-id]')
    rows.forEach(row => {
      const equipmentId = row.dataset.equipmentId
      const cells = row.querySelectorAll('td')

      this.equipmentData.set(equipmentId, {
        id: equipmentId,
        typeName: cells[0]?.textContent?.trim() || '',
        name: cells[1]?.textContent?.trim() || '',
        maker: cells[2]?.textContent?.trim() || '',
        serialNo: cells[3]?.textContent?.trim() || ''
      })
    })

    console.log('[equipment] Loaded equipment:', this.equipmentData.size)
  }

  /**
   * Add equipment to the report
   */
  async addEquipment(event) {
    event.preventDefault()

    // Find the select element from the button's parent row
    const button = event.currentTarget
    const row = button.closest('.row')
    const selectElement = row.querySelector('select[data-equipment-target="equipmentSelect"]')

    if (!selectElement) {
      console.error('[equipment] Could not find select element')
      return
    }

    const equipmentId = selectElement.value

    if (!equipmentId) {
      alert('Please select equipment first')
      return
    }

    // Check if equipment is already added
    if (this.equipmentData.has(equipmentId)) {
      alert('This equipment is already added to the report')
      selectElement.value = ''
      return
    }

    // Fetch equipment data from server
    try {
      const response = await fetch(`/equipment/${equipmentId}.json`)
      if (!response.ok) throw new Error('Failed to fetch equipment data')

      const equipmentData = await response.json()

      // Add to our data map
      this.equipmentData.set(equipmentId, {
        id: equipmentId,
        typeName: equipmentData.equipment_type_name || '',
        name: equipmentData.name || '',
        maker: equipmentData.maker || '',
        serialNo: equipmentData.serial_no || ''
      })

      // Update UI
      this.renderEquipment()

      // Reset select
      selectElement.value = ''

      // Trigger select2 change if it exists
      if (window.jQuery && window.jQuery(selectElement).data('select2')) {
        window.jQuery(selectElement).val('').trigger('change')
      }

      console.log('[equipment] Equipment added:', equipmentId)

      // Dispatch custom event for other controllers (e.g., ET auto-populate)
      const addedEquipmentData = this.equipmentData.get(equipmentId)
      document.dispatchEvent(new CustomEvent('equipment:added', {
        detail: {
          equipmentId: equipmentId,
          equipmentTypeName: equipmentData.equipment_type_name || addedEquipmentData.typeName,
          equipmentData: equipmentData
        }
      }))
      console.log('[equipment] Dispatched equipment:added event:', equipmentId, equipmentData.equipment_type_name)

      // Trigger autosave
      await this.triggerAutosave()
    } catch (error) {
      console.error('[equipment] Error adding equipment:', error)
      alert('Failed to add equipment. Please try again.')
    }
  }

  /**
   * Remove equipment from the report
   */
  async removeEquipment(event) {
    event.preventDefault()

    const button = event.currentTarget
    const equipmentId = button.dataset.equipmentId

    if (!equipmentId) return

    // Get equipment data before removing (for event dispatch)
    const removedData = this.equipmentData.get(equipmentId)

    // Remove from data map
    this.equipmentData.delete(equipmentId)

    // Update UI
    this.renderEquipment()

    console.log('[equipment] Equipment removed:', equipmentId)

    // Dispatch custom event for other controllers (e.g., ET field clearing)
    document.dispatchEvent(new CustomEvent('equipment:removed', {
      detail: {
        equipmentId: equipmentId,
        equipmentTypeName: removedData ? removedData.typeName : null,
        equipmentData: removedData
      }
    }))
    console.log('[equipment] Dispatched equipment:removed event:', equipmentId)

    // Trigger autosave
    await this.triggerAutosave()
  }

  /**
   * Render the equipment table
   */
  renderEquipment() {
    // Check if we have any equipment
    if (this.equipmentData.size === 0) {
      this.containerTarget.innerHTML = `
        <div class="text-center text-muted py-3">
          <i class="fa fa-wrench mr-2"></i>
          No equipment added yet
        </div>
      `
      this.updateHiddenFields()
      return
    }

    // Separate by type (Multiple/Single) if needed
    // For now, just render all in one table
    let tableHTML = `
      <div class="equipment-list-container">
        <div class="table-responsive">
          <table class="table table-sm table-hover">
            <thead>
              <tr>
                <th>Equipment Type</th>
                <th>Name</th>
                <th>Maker</th>
                <th>Serial No.</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody data-equipment-target="tableBody">
    `

    // Add rows for each equipment
    this.equipmentData.forEach((equipment, equipmentId) => {
      tableHTML += `
        <tr data-equipment-id="${equipmentId}">
          <td>${this.escapeHtml(equipment.typeName)}</td>
          <td>${this.escapeHtml(equipment.name)}</td>
          <td>${this.escapeHtml(equipment.maker)}</td>
          <td>${this.escapeHtml(equipment.serialNo)}</td>
          <td>
            <button class="btn btn-sm btn-link text-danger p-0" type="button"
                    data-action="click->equipment#removeEquipment"
                    data-equipment-id="${equipmentId}"
                    title="Remove">
              <i class="fa fa-trash"></i>
            </button>
          </td>
        </tr>
      `
    })

    tableHTML += `
            </tbody>
          </table>
        </div>
      </div>
    `

    this.containerTarget.innerHTML = tableHTML

    // Update hidden fields
    this.updateHiddenFields()
  }

  /**
   * Update hidden fields with current equipment IDs
   */
  updateHiddenFields() {
    if (!this.hasHiddenFieldsTarget) return

    let hiddenHTML = ''
    this.equipmentData.forEach((equipment, equipmentId) => {
      hiddenHTML += `<input type="hidden" name="report[equipment_ids][]" value="${equipmentId}" />\n`
    })

    this.hiddenFieldsTarget.innerHTML = hiddenHTML
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * Trigger autosave via outlet
   */
  async triggerAutosave() {
    console.log('╔═══════════════════════════════════════════╗')
    console.log('║     EQUIPMENT CHANGE - TRIGGER SAVE       ║')
    console.log('╚═══════════════════════════════════════════╝')
    console.log('[equipment] Has autosave outlet:', this.hasAutosaveOutlet)

    if (this.hasAutosaveOutlet) {
      console.log('[equipment] ✓ Autosave outlet found, calling saveNow()...')
      console.log('[equipment] Outlet:', this.autosaveOutlet)
      await this.autosaveOutlet.saveNow()
      console.log('[equipment] ✅ Equipment save complete')
    } else {
      console.error('[equipment] ❌ NO AUTOSAVE OUTLET AVAILABLE!')
      console.log('[equipment] Available outlets:', Object.keys(this).filter(k => k.includes('Outlet')))
    }
  }
}
